import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useWorkflow } from '../workflow/hooks';
import { processShipmentScopeForItem } from '../utils/shipmentScopeUtils';
import type { WorkflowData } from '../workflow/schemaTypes';

/**
 * Custom hook to handle shipment scope logic when reasons are selected.
 * This centralizes the business logic and keeps components focused on rendering.
 * Optimized to only process the specific item that changed for better performance.
 */
export const useShipmentScope = (itemId?: string) => {
  const { order, updateData } = useWorkflow();
  const { getValues } = useFormContext<WorkflowData>();

  /**
   * Handles shipment scope logic when a reason is selected for a specific item.
   * This is optimized to only process the changed item, not all items.
   */
  const handleReasonChange = useCallback(() => {
    if (!order || !itemId) return;

    // Get current form data and trigger shipment scope logic for this specific item
    const currentFormData = getValues();
    if (currentFormData) {
      processShipmentScopeForItem(currentFormData, order, itemId, updateData);
    }
  }, [order, itemId, getValues, updateData]);

  return {
    handleReasonChange,
  };
};
